import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import ExploreViewType from '@alltrails/amplitude/enums/ExploreViewType';
import useMap from '@alltrails/maps/hooks/useMap';
import type { AllTrailsBounds, AllTrailsCenter, LngLat } from '@alltrails/maps/types/Geo';

type PaddingOptions = ReturnType<NonNullable<ReturnType<typeof useMap>>['getPadding']>;

export type ExploreState = {
  exploreViewType?: ExploreViewType;
  headerHeight?: number;
  initialMapBounds?: AllTrailsBounds;
  initialMapCenter?: AllTrailsCenter;
  isMobileListAtTop: boolean;
  isShowingMobileList: boolean;
  mapPadding?: PaddingOptions;
  openDistanceAwayOnNewLocationResult?: boolean;
  panelCollapsed: boolean;
  returningBounds?: AllTrailsBounds;
  showFilterBar?: boolean;
  showRecenterButton?: boolean;
  streetAddressLngLat?: LngLat;
};

export const initialState: ExploreState = {
  isMobileListAtTop: true,
  isShowingMobileList: false,
  panelCollapsed: false
};

export const exploreSlice = createSlice({
  name: 'explore',
  initialState,
  reducers: {
    resetState: (state: ExploreState, action: PayloadAction<{ persistKeys?: (keyof ExploreState)[]; initialState?: Partial<ExploreState> }>) => {
      const defaultState: ExploreState = { ...initialState, ...(action.payload.initialState || {}) };
      action.payload.persistKeys?.forEach(key => {
        const persistValue = state[key];
        (defaultState[key] as typeof persistValue) = persistValue;
      });

      return defaultState;
    },
    setExploreViewType: (state, action: { payload: ExploreViewType | undefined }) => {
      state.exploreViewType = action.payload;
    },
    setHeaderHeight: (state, action: { payload: number }) => {
      state.headerHeight = action.payload;
    },
    setInitialMapBounds: (state, action: { payload: AllTrailsBounds }) => {
      state.initialMapBounds = action.payload;
    },
    setInitialMapCenter: (state, action: { payload: AllTrailsCenter }) => {
      state.initialMapCenter = action.payload;
    },
    setIsMobileListAtTop: (state, action: { payload: boolean }) => {
      state.isMobileListAtTop = action.payload;
    },
    setIsShowingMobileList: (state, action: { payload: boolean }) => {
      state.isShowingMobileList = action.payload;
    },
    setMapPadding: (state, action: { payload: PaddingOptions }) => {
      state.mapPadding = action.payload;
    },
    setPanelCollapsed: (state, action: { payload: boolean }) => {
      state.panelCollapsed = action.payload;
    },
    setReturningBounds: (state, action: { payload: AllTrailsBounds }) => {
      state.returningBounds = action.payload;
    },
    setShowRecenterButton: (state, action: { payload: boolean }) => {
      state.showRecenterButton = action.payload;
    },
    setStreetAddressLngLat: (state, action: { payload: LngLat | undefined }) => {
      state.streetAddressLngLat = action.payload;
    }
  }
});

export const {
  resetState,
  setExploreViewType,
  setHeaderHeight,
  setInitialMapBounds,
  setInitialMapCenter,
  setIsMobileListAtTop,
  setIsShowingMobileList,
  setMapPadding,
  setPanelCollapsed,
  setReturningBounds,
  setShowRecenterButton,
  setStreetAddressLngLat
} = exploreSlice.actions;
export default exploreSlice.reducer;
